export interface ILatLng {
  latitude: number;
  longitude: number;
  unableToFind?: boolean;
}

export const UNABLE_TO_FIND_LOCATION: ILatLng = {
  latitude: 0,
  longitude: 0,
  unableToFind: true,
};

export const extractLatLng = (source: unknown): ILatLng | null => {
  if (!source || typeof source !== 'object') return null;
  const s = source as { lat: unknown; lng: unknown };
  const lat = typeof s.lat === 'function' ? s.lat() : s.lat;
  const lng = typeof s.lng === 'function' ? s.lng() : s.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  return { latitude: lat, longitude: lng };
};

export const parseLatLng = (source: unknown): ILatLng | null => {
  if (!source) return null;
  if (typeof source === 'object') {
    const s = source as Record<string, unknown>;
    const lat = Number(s.latitude ?? s.lat);
    const lng = Number(s.longitude ?? s.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng))
      return { latitude: lat, longitude: lng };
    return null;
  }
  if (typeof source !== 'string') return null;
  try {
    return parseLatLng(JSON.parse(source));
  } catch {
    const m = source.match(
      /lat:\s*(-?\d+(?:\.\d+)?)\s*,\s*lng:\s*(-?\d+(?:\.\d+)?)/i
    );
    return m ? { latitude: Number(m[1]), longitude: Number(m[2]) } : null;
  }
};

export const serializeLatLngOtherInfoField = (
  coords: ILatLng | null | undefined
): string | null => {
  if (coords == null) {
    return null;
  }
  return JSON.stringify(coords);
};

function isLatLngPlainObject(value: unknown): value is ILatLng {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as Record<string, unknown>;
  return typeof o.latitude === 'number' && typeof o.longitude === 'number';
}

function parseLatLngObjectFromString(value: string): ILatLng | null {
  try {
    const once = JSON.parse(value) as unknown;
    if (typeof once === 'string') {
      return parseLatLngObjectFromString(once);
    }
    if (isLatLngPlainObject(once)) {
      return once.unableToFind === true
        ? {
            latitude: once.latitude,
            longitude: once.longitude,
            unableToFind: true,
          }
        : { latitude: once.latitude, longitude: once.longitude };
    }
    return null;
  } catch {
    return null;
  }
}

export function normalizeLocationLeafForPersist(value: unknown): unknown {
  if (value === null || value === undefined || value === '') {
    return value;
  }
  if (isLatLngPlainObject(value)) {
    return value.unableToFind === true
      ? {
          latitude: value.latitude,
          longitude: value.longitude,
          unableToFind: true,
        }
      : { latitude: value.latitude, longitude: value.longitude };
  }
  if (typeof value === 'string') {
    const parsed = parseLatLngObjectFromString(value);
    if (parsed) {
      return parsed;
    }
    return value;
  }
  return value;
}

export function normalizeLatLngOtherInfoLocationsForPersist(
  values: Record<string, any>
): Record<string, any> {
  const walk = (val: unknown): unknown => {
    if (val === null || val === undefined) {
      return val;
    }
    const t = typeof val;
    if (t !== 'object') {
      return val;
    }
    if (Array.isArray(val)) {
      return val.map((entry) => walk(entry));
    }
    if (val instanceof Date) {
      return val;
    }

    const obj = val as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(obj)) {
      if (key.endsWith('_Location')) {
        out[key] = normalizeLocationLeafForPersist(inner);
      } else if (
        inner !== null &&
        typeof inner === 'object' &&
        !(inner instanceof Date)
      ) {
        out[key] = walk(inner);
      } else {
        out[key] = inner;
      }
    }
    return out;
  };

  return walk(values) as Record<string, any>;
}
